import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import styled from "styled-components";
import { Button } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import InputIcon from "@mui/icons-material/Input";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTypedSelector } from "store";
import OverlayRightPanel from "containers/OverlayRightPanel/OverlayRightPanel.container";
import { getProductsAction } from "store/reducers/products/products.actions";
import ProductLinesRegionFilter from "../../components/ProductLinesRegionFilter.component";
import { useDispatch } from "react-redux";
import AddProductLineForm from "./AddProductLineForm";
import ImportProductForm from "./ImportProductForm";
import ImportRuleEngineJsonForm from "./ImportRuleEngineJsonForm";
import SearchBarProductLines from "./SearchProductLines";
import ProductsGridView from "./ProductsGridView";
import ProductLinePagination from "./ProductLinePagination";
import { ProductRegion } from "../../../../store/reducers/products/products.types";
import UploadRulesEngineJsonButton from "../ProductDetail/ProductOverview/UploadRulesEngineJsonButton";

export default function ProductsList() {
  // local state
  const [searchTermProductLine, setSearchTermProductLine] = useState("");
  const [searchRegions, setSearchRegions] = useState<Array<ProductRegion>>([]);
  const [searchPage, setSearchPage] = useState<number>(0);
  const [showAddProductLineForm, setShowAddProductLineForm] =
    useState<boolean>(false);
  const [showImportDatabaseJsonForm, setShowImportDatabaseJsonForm] =
    useState<boolean>(false);
  const [showImportRuleEngineJsonForm, setShowImportRuleEngineJsonForm] =
    useState<boolean>(false);

  const handleChange = (e) => {
    setSearchTermProductLine(e.target.value);
  };

  // hooks
  const dispatch = useDispatch();

  // selectors
  const { navigationOpen } = useTypedSelector(
    (state) => state.user.preferences
  );
  const { metadata, getting } = useTypedSelector((state) => state.products);

  // handlers
  const handleAddProductLinePress = () => {
    setShowAddProductLineForm(!showAddProductLineForm);
  };

  const handleImportProductLinePress = () => {
    setShowImportDatabaseJsonForm(!showImportDatabaseJsonForm);
  };

  const handleImportRuleEngineJsonPress = () => {
    setShowImportRuleEngineJsonForm(!showImportRuleEngineJsonForm);
  };

  const handleGetProduct = (searchTerm, regions, page = 0) =>
    dispatch(
      getProductsAction({
        items: "10",
        name: searchTerm,
        page,
        regions: regions,
        sortBy: "name",
      })
    );

  // debounce
  const debouncedGetProducts = useCallback(
    debounce(
      (searchTerm, regions, page) =>
        handleGetProduct(searchTerm, regions, page),
      500,
      {
        leading: false,
        trailing: true,
      }
    ),
    []
  );

  //Wraps the debouncedGetProduct
  const refreshProductLines = useCallback(() => {
    handleGetProduct(searchTermProductLine, searchRegions, searchPage);
  }, [handleGetProduct, searchTermProductLine, searchRegions, searchPage]);

  useEffect(() => {
    debouncedGetProducts(searchTermProductLine, searchRegions, searchPage);
  }, [searchPage]);

  // when a change in search term or region happens, reset the page to initial value
  useEffect(() => {
    setSearchPage(0);
    debouncedGetProducts(searchTermProductLine, searchRegions, 0);
  }, [searchTermProductLine, searchRegions]);

  const doNotDisplayLoader = searchTermProductLine?.length > 0;

  return (
    <Wrapper navigationOpen={navigationOpen}>
      {showAddProductLineForm && (
        <OverlayRightPanel
          open={showAddProductLineForm}
          closeNav={handleAddProductLinePress}
          title="Add New Productline"
        >
          <AddProductLineForm
            handleRefreshProductlines={refreshProductLines}
            visible={showAddProductLineForm}
            handleCloseForm={() => setShowAddProductLineForm(false)}
          />
        </OverlayRightPanel>
      )}

      {showImportDatabaseJsonForm && (
        <OverlayRightPanel
          open={showImportDatabaseJsonForm}
          closeNav={handleImportProductLinePress}
          title="Import Productline Database JSON"
        >
          <ImportProductForm
            handleRefreshProductlines={refreshProductLines}
            visible={showImportDatabaseJsonForm}
          />
        </OverlayRightPanel>
      )}

      {showImportRuleEngineJsonForm && (
        <OverlayRightPanel
          open={showImportRuleEngineJsonForm}
          closeNav={handleImportRuleEngineJsonPress}
          title="Import Rules Engine JSON"
        >
          <ImportRuleEngineJsonForm
            handleRefreshProductlines={refreshProductLines}
            visible={showImportRuleEngineJsonForm}
          />
        </OverlayRightPanel>
      )}

      <Grid container spacing={2} display="flex" alignItems="center">
        <Grid xs={3}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddProductLinePress}
          >
            Add new productline
          </Button>
        </Grid>
        <Grid xs={3}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InputIcon />}
            onClick={handleImportProductLinePress}
          >
            Import productline from Database JSON
          </Button>
        </Grid>
        <Grid xs={3}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InputIcon />}
            onClick={handleImportRuleEngineJsonPress}
          >
            Import productline from Rules Engine JSON
          </Button>
        </Grid>
        <Grid xs={3}>
          <UploadRulesEngineJsonButton executeOnMenuItemSelect />
        </Grid>
      </Grid>
      <Grid container spacing={1} display="flex" alignItems="center">
        <Grid xs={3}>
          <ProductLinesRegionFilter
            searchRegions={searchRegions}
            setSearchRegions={setSearchRegions}
          />
        </Grid>
        <Grid xs={9}>
          <SearchBarProductLines
            searchTermProductLine={searchTermProductLine}
            handleChange={handleChange}
          />
        </Grid>
      </Grid>

      <ProductLineListWrapper>
        <ProductsGridView doNotDisplayLoader={doNotDisplayLoader} />
      </ProductLineListWrapper>
      {!getting && (
        <ProductLinePagination
          metadata={metadata}
          setSearchPage={setSearchPage}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div<any>`
  padding-left: 10px;
  padding-top: 10px;
  padding-right: 40px;
  min-width: 900px;

  overflow-y: auto;

  @media ${(props) => props.theme.device.mobile} {
    padding-left: ${(props) => (props.navigationOpen ? "50px" : "24px")};
  }
`;

const ProductLineListWrapper = styled.div<any>`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  min-height: 200px;
`;
